  import React, { useState, useEffect, useRef } from 'react';
  import { capitalizeName, isObject } from '../../../utils/commonUtils';
  import FormInputs from '../../../components/UI/FormInputs';
  import WithDropdownState from '../../../hoc/WithDropdownState';
  import VisitsService from '../../../services/VisitsService';
import EmrVisitsService from '../../../services/EmrVisitsService';
import Alert from '../../../components/UI/ConfirmBox/Alert';
import VoiceIcon from '../../../components/UI/Icons/VoiceIcon';

  const SubTabSection = (props) => {
    const {
      subTabs,
      selectedSubTab,
      onSubTabClick,
      finishedSubTabs,
      finishedTabs,
      tabHeaders,
      visit_status,
      setVisitStatus,
      visitId,
      fetchProgressNotesByScreen,
      selectedTab,
      onOpenVoiceTranscription,
    } = props;

    const [loading, setLoading] = useState(false);
    const [showStartOverConfirm, setShowStartOverConfirm] = useState(false);
    const [showRetrieveConfirm, setShowRetrieveConfirm] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const dropdownRef = useRef(null);

    const openTranscriptionModal = () => {
      if (!visitId) return;
      onOpenVoiceTranscription?.();
    };
    const isCompleted = tabHeaders.every((item) => finishedTabs.includes(item.label));
    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
          setShowDropdown(false);
        }
      };

      if (showDropdown) {
        document.addEventListener('mousedown', handleClickOutside);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [showDropdown]);

    // Filter out CHART_BY_SUPERBILL and CHART_BY_DICTATION from regular subtabs in Examination tab
    const chartSubTabs = subTabs.filter(
      (sub) => sub.apikey === 'CHART_BY_SUPERBILL' || sub.apikey === 'CHART_BY_DICTATION'
    );
    const regularSubTabs = subTabs.filter(
      (sub) => sub.apikey !== 'CHART_BY_SUPERBILL' && sub.apikey !== 'CHART_BY_DICTATION'
    );
    
    // Use filtered subtabs only for Examination tab, otherwise use all subtabs
    const displaySubTabs = selectedTab === 'Examination' ? regularSubTabs : subTabs;
    const shouldShowDropdown = selectedTab === 'Examination' && chartSubTabs.length > 0;

    // ✅ Helper for status badge
    const statusDetector = (status) => {
      return (
        <div
          className={`badge ${
            status.toLowerCase() === 'passed'
              ? 'sh_badgeSuccess'
              : status.toLowerCase() === 'not started'
              ? 'sh_badgePending'
              : status.toLowerCase() === 'failed'
              ? 'sh_badgeDanger'
              : 'sh_badgeWarning'
          }`}
        >
          <span className="pr-1">
            <svg
              width="8"
              height="8"
              viewBox="0 0 8 8"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <ellipse
                cx="4"
                cy="4"
                rx="4"
                ry="4"
                fill={`${
                  status.toLowerCase() === 'passed'
                    ? '#0F8647'
                    : status.toLowerCase() === 'not started'
                    ? '#1D2433CC'
                    : status.toLowerCase() === 'failed'
                    ? '#E02D3C'
                    : '#CA8B14'
                }`}
              ></ellipse>
            </svg>
          </span>
          {capitalizeName(status.toLowerCase())}
        </div>
      );
    };

    // ✅ Function to call API when a field (like visit_status) changes
    const updateFeildData = async (key, id, value) => {
      const payload = { [key]: value };
      let response;
      let validation = {};
      let succMsg = 'Updated Successfully';

      try {
        setLoading(true);

        if (key === 'visit_status') {
          response = await VisitsService.updateVisitStatus(null, id, payload);
          succMsg = 'Visit Status Updated Successfully';
        } else if (key === 'billing_status') {
          response = await VisitsService.updateVisitBillingStatus(id, payload);
          succMsg = 'Billing Status Updated Successfully';
        }

        if (response?.status === 'ERROR') {
          if (isObject(response.data)) {
            for (let key in response.data) {
              validation = {
                ...validation,
                [key]: response.data[key].join(','),
              };
            }
          }
        } else {
          props.Toast?.success?.(succMsg);
          props.fetchVisitsList?.({}, false); // optional refresh if parent supports it
        }
      } catch (err) {
        console.error('Error updating field:', err);
        props.Toast?.error?.('Error while updating visit status');
      } finally {
        setLoading(false);
      }

      return validation;
    };

    // ✅ Dropdown change handler
    const handleStatusChange = async (val) => {
        await updateFeildData('visit_status', visitId, val);
        setVisitStatus(val);
        };
        const handlePopulateValues = () => {
          setShowRetrieveConfirm(true);
        };

        const handleRetrieveConfirm = async () => {
          setShowRetrieveConfirm(false);
          try {
              console.log("Props", props);
              const employeeId = props?.visitDetails?.employee?.employee_id;
              const visitId = props?.visitDetails?.visit_id;
              const visitCategory = props?.visitDetails?.visit_category;

              if (!employeeId || !visitId) {
              props.Toast.error("Employee or Visit ID not found");
              return;
              }

              props.Loader(true);
              
              // Determine which API to use based on visit category
              const workCompCategories = ['Work Comp - Follow Up', 'Work Comp - Other'];
              const newCategories = ['Urgent Care - Injury', 'Urgent Care - Illness', 'Urgent Care - Follow Up', 'Urgent Care - Cash Patient', 'Urgent Care - Telehealth',
                'Private - Mental Health Exam', 'Private - Primary Care - Initial', 'Private - Primary Care - Follow Up'];
              
              let response;
              if (workCompCategories.includes(visitCategory)) {
                // Use existing incident-based API for Work Comp
                response = await EmrVisitsService.savePreVisitData(employeeId, visitId);
              } else if (newCategories.includes(visitCategory)) {
                // Use new most recent visit API for new categories
                response = await EmrVisitsService.saveMostRecentVisitData(employeeId, visitId);
              } else {
                props.Toast.error("This feature is not available for this visit category");
                return;
              }

              // ✅ Handle success or error from API
              if (response?.error) {
              props.Toast.error(response.message || "No Previous Visit Found");
              } else if (response?.status === 400 || response?.code === 400) {
              props.Toast.error(response.message || "No Previous Visit Found");
              } else {
              props.Toast.success("Values populated successfully");
              // Refresh incident data
              // Refresh tab status to see changes in real time
              if (props.fetchTabsList) {
                await props.fetchTabsList();
              }
              // Refresh all EMR fields after data is populated
              if (props.refreshEmrFields) {
                props.refreshEmrFields();
              }
              // Refresh progress notes after data is populated
              if (fetchProgressNotesByScreen) {
                await fetchProgressNotesByScreen();
              }
              
              }

          } catch (error) {
              props.Toast.error(error.response?.data?.message || error.message || "Failed to populate values");
          } finally {
              props.Loader(false);
          }
        };

        const handleRetrieveCancel = () => {
          setShowRetrieveConfirm(false);
        };;
    const handleStartOver = () => {
      setShowStartOverConfirm(true);
    };

    const handleStartOverConfirm = async () => {
      setShowStartOverConfirm(false);
      try {
        const visitId = props?.visitDetails?.visit_id;

        if (!visitId) {
          props.Toast.error("Visit ID not found");
          return;
        }

        const response = await EmrVisitsService.clearVisitData(visitId);
        if (response?.error) {
          props.Toast.error(response.message || "Failed to clear visit data");
        } else {
          props.getIncidentById();
          // Refresh EMR fields using the refresh callback
          if (props.refreshEmrFields) {
            props.refreshEmrFields();
          }
          props.Toast.success("Visit data cleared successfully");

        }
      } catch (error) {
        console.error("Error clearing visit data:", error);
        props.Toast?.error?.("Error clearing visit data");
      }
    };

    const handleStartOverCancel = () => {
      setShowStartOverConfirm(false);
    };


    return (
      <>
        <div className="sh_cardHeader">
        <div className="form-group mb-0 d-flex justify-content-between align-items-center">
          {/* Left Side — Tabs */}
          <div>
            <div className="tabs_wrapForTable">
              <ul className="nav sh_subnav border-0">
                {displaySubTabs.map(({ apikey, label }, index) => (
                  <li key={index}>
                    <a
                      className={`cursor-pointer flex-sm-fill text-sm-center nav-link ${
                        selectedSubTab === apikey ? 'active' : ''
                      }`}
                      aria-current="page"
                      title={label}
                      onClick={() => onSubTabClick(apikey)}
                    >
                      {label}
                      {finishedSubTabs.includes(apikey) && (
                        <span style={{ margin: '0 4px' }}>
                          <svg width="16" 
                          height="16" 
                          viewBox="0 0 20 20" 
                          fill="none" 
                          xmlns="http://www.w3.org/2000/svg">
                          <path d="M16.3006 6.20337L10.0039 12.5L6.85645 9.3517" stroke="#0AC23D" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                          <path d="M17.5 10C17.5 14.1425 14.1425 17.5 10 17.5C5.8575 17.5 2.5 14.1425 2.5 10C2.5 5.8575 5.8575 2.5 10 2.5C11.2108 2.5 12.3508 2.79417 13.3633 3.30333"
                          stroke="#0AC23D" 
                          stroke-width="1.5" 
                          stroke-linecap="round" 
                          stroke-linejoin="round"/>
                          </svg>
                        </span>
                      )}
                    </a>
                  </li>
                ))}
                {/* Click Dropdown Button for SnapSheet and Dictation in Examination tab */}
                {shouldShowDropdown && (
                  <li 
                    ref={dropdownRef}
                    style={{ position: 'relative' }}
                  >
                    <a
                      className={`cursor-pointer flex-sm-fill  nav-link ${
                        chartSubTabs.some(sub => sub.apikey === selectedSubTab) ? 'active' : ''
                      }`}
                      style={{ position: 'relative', fontSize: '0.7rem' }}
                      onClick={(e) => {
                        e.preventDefault();
                        setShowDropdown(!showDropdown);
                      }}
                    >
                      SnapSheets
                      {chartSubTabs.some(sub => finishedSubTabs.includes(sub.apikey)) && (
                           <span style={{ margin: '0 4px' }}>
                          <svg width="16"
                          height="16"
                          viewBox="0 0 20 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg">
                          <path d="M16.3006 6.20337L10.0039 12.5L6.85645 9.3517" stroke="#0AC23D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M17.5 10C17.5 14.1425 14.1425 17.5 10 17.5C5.8575 17.5 2.5 14.1425 2.5 10C2.5 5.8575 5.8575 2.5 10 2.5C11.2108 2.5 12.3508 2.79417 13.3633 3.30333"
                          stroke="#0AC23D"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"/>
                          </svg>
                           </span>
                      )}
                      {showDropdown && (
                        <div
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            backgroundColor: '#fff',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            zIndex: 1000,
                            minWidth: '160px',
                            marginTop: '4px',
                          }}
                        >
                          {chartSubTabs.map(({ apikey, label }, idx) => {
                            // Map labels: CHART_BY_SUPERBILL -> "SnapSheet", CHART_BY_DICTATION -> "Dictation"
                            const displayLabel = apikey === 'CHART_BY_SUPERBILL' 
                              ? 'SmartSheets' 
                              : apikey === 'CHART_BY_DICTATION' 
                              ? 'Chart by Dictation' 
                              : label;
                            
                            return (
                              <div
                                key={idx}
                                onClick={() => {
                                  onSubTabClick(apikey);
                                  setShowDropdown(false);
                                }}
                                style={{
                                  padding: '10px 15px',
                                  cursor: 'pointer',
                                  backgroundColor: selectedSubTab === apikey ? '#f0f0f0' : '#fff',
                                  borderBottom: idx < chartSubTabs.length - 1 ? '1px solid #eee' : 'none',
                                  transition: 'background-color 0.2s',
                                }}
                                onMouseEnter={(e) => {
                                  if (selectedSubTab !== apikey) {
                                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = selectedSubTab === apikey ? '#f0f0f0' : '#fff';
                                }}
                              >
                                {displayLabel}
                                {finishedSubTabs.includes(apikey) && (
                                  <span style={{ marginLeft: '8px' }}>
                                    <svg width="14" 
                                    height="14" 
                                    viewBox="0 0 20 20" 
                                    fill="none" 
                                    xmlns="http://www.w3.org/2000/svg">
                                    <path d="M16.3006 6.20337L10.0039 12.5L6.85645 9.3517" stroke="#0AC23D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M17.5 10C17.5 14.1425 14.1425 17.5 10 17.5C5.8575 17.5 2.5 14.1425 2.5 10C2.5 5.8575 5.8575 2.5 10 2.5C11.2108 2.5 12.3508 2.79417 13.3633 3.30333"
                                    stroke="#0AC23D" 
                                    strokeWidth="1.5" 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round"/>
                                    </svg>
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </a>
                  </li>
                )}
              </ul>
            </div>
          </div>

          {/* Right Side — Status Dropdown */}
          <div className="d-flex align-items-center">
          <div className="d-flex align-items-center">

{[
  'Work Comp - New Injury',
  'Work Comp - Other',
  'Work Comp - Follow Up',
  'Urgent Care - Injury',
  'Urgent Care - Illness',
  'Urgent Care - Follow Up',
  'Urgent Care - Cash Patient',
  'Urgent Care - Telehealth',
  'Private - Mental Health Exam',
  'Private - Primary Care - Initial',
  'Private - Primary Care - Follow Up',
].includes(props?.visitDetails?.visit_category) && (
  <>
    {/* <button 
      className="btn btn-primary mr-2" 
      onClick={handleStartOver}
    >
      Start Over
    </button> */}

    {(((props?.visitDetails?.visit_category === 'Work Comp - Follow Up' || props?.visitDetails?.visit_category === 'Work Comp - Other') && selectedSubTab === 'COMPLAINT') ||
    (['Urgent Care - Injury', 'Urgent Care - Illness', 'Urgent Care - Follow Up', 'Urgent Care - Cash Patient', 'Urgent Care - Telehealth',
      'Private - Mental Health Exam', 'Private - Primary Care - Initial', 'Private - Primary Care - Follow Up'].includes(props?.visitDetails?.visit_category) && selectedSubTab === 'COMPLAINT')) && (
      <button 
        className="btn btn-primary mr-3" 
        onClick={handlePopulateValues}
      >
        Retrieve Previous Info
      </button>
    )}
  </>
)}

{onOpenVoiceTranscription &&
  (selectedSubTab === "COMPLAINT" || selectedSubTab === "SUBJECTIVE_COMPLAINT") && (
    <button
      type="button"
      className="btn btn-primary mr-2"
      onClick={openTranscriptionModal}
      title="Open transcription"
      aria-label="Open transcription"
      style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
    >
      <span className="d-inline-flex align-items-center" aria-hidden>
        <VoiceIcon size={20} />
      </span>
      Transcribe
    </button>
  )}

<h6 className="mb-0 mx-1 badgelabel" style={{ fontWeight: '700' }}>Status:</h6>
<div style={{ minWidth: '120px' }}>
  <FormInputs
    type="SelectList"
    value={visit_status}
    changeHandler={handleStatusChange}
    options={props.visitStatus}
    className="form-control form-control-sm"
    disabled={loading}
  />
</div>
</div>

            {loading && (
              <div className="mx-2 spinner-border spinner-border-sm text-secondary" role="status" />
            )}
          </div>
        </div>
      </div>
      {showStartOverConfirm && (
        <Alert
          contentTitle="Are you sure you want to start over?"
          content="This action will clear all visit data. This cannot be undone."
          onClose={handleStartOverCancel}
          onDelete={handleStartOverConfirm}
          type="warning"
          yesButtonName="Yes"
          noButtonName="No"
        />
      )}
      {showRetrieveConfirm && (
        <Alert
          contentTitle="Retrieve Previous Visit Info?"
          content="This will populate data from the previous mapped visit. Are you sure you want to continue?"
          onClose={handleRetrieveCancel}
          onDelete={handleRetrieveConfirm}
          type="warning"
          yesButtonName="Yes"
          noButtonName="No"
        />
      )}
    </>
  );
  };

  export default WithDropdownState(SubTabSection);
